import { useCallback, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useJobs, useDismissJobNoGo } from "@/hooks/useJobs";
import {
  usePipelines,
  useCreatePipeline,
  useArchivePipeline,
  useRestorePipeline,
} from "@/hooks/usePipeline";
import { useClientPagination } from "@/hooks/usePagination";
import { PaginationBar } from "@/components/pagination-bar";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { resolveJobScore } from "@/format";
import { cn } from "@/cn";
import type { Job, Pipeline } from "@career-ops-amplify/shared";
import { getPipelineForJob } from "@/lib/pipeline-analytics";
import { canArchivePipeline, isPipelineArchived } from "@/lib/pipeline-stages";
import { isInboxJob } from "@/lib/job-ui";
import { JOB_BUCKET_GLOSSARY } from "@/lib/job-bucket-glossary";
import { parseNoGoPipelineConflict } from "@/lib/no-go-error";
import { decodeHtmlEntities } from "@/lib/html-entities";
import {
  CollectionCommandBar,
  DataTable,
  EntityCard,
  EntityCompactItem,
  EntityListLayout,
  useClientTableSort,
  useListViewMode,
  type Column,
  type EntitySortOption,
} from "@/components/collection";

function displayCompany(job: Job): string {
  return decodeHtmlEntities(job.company ?? "");
}

function displayRole(job: Job): string {
  return decodeHtmlEntities(job.role ?? "");
}

const TONE_BG: Record<string, string> = {
  good: "bg-emerald-500/10 text-emerald-400",
  warn: "bg-amber-500/10 text-amber-400",
  bad: "bg-red-500/10 text-red-400",
  muted: "bg-zinc-500/10 text-muted-foreground",
};

type Tab = "pipeline" | "unpiped" | "archived";

const ALLOWED_MODES = ["table", "list", "cards"] as const;

const SORT_OPTIONS: EntitySortOption[] = [
  { field: "company", order: "asc", label: "Company A–Z" },
  { field: "role", order: "asc", label: "Role A–Z" },
  { field: "updated", order: "desc", label: "Recently updated" },
];

function pipelineHref(pipelineId: string, stage?: string): string {
  const base = `/pipeline/${pipelineId}`;
  return stage ? `${base}?stage=${encodeURIComponent(stage)}` : base;
}

function pipelineRecordId(pipeline: Pipeline): string {
  return pipeline.pipelineId ?? pipeline.id;
}

function applySort(jobs: Job[], sort: EntitySortOption): Job[] {
  const dir = sort.order === "asc" ? 1 : -1;
  return [...jobs].sort((a, b) => {
    if (sort.field === "role")
      return displayRole(a).localeCompare(displayRole(b)) * dir;
    if (sort.field === "updated") {
      return (
        (new Date(a.updatedAt ?? a.createdAt).getTime() -
          new Date(b.updatedAt ?? b.createdAt).getTime()) *
        dir
      );
    }
    return displayCompany(a).localeCompare(displayCompany(b)) * dir;
  });
}

export function PipelinePage() {
  const { data: jobsData, isLoading: jobsLoading } = useJobs();
  const { data: pipelinesData, isLoading: pipelinesLoading } = usePipelines();
  const createPipelineMutation = useCreatePipeline();
  const archiveMutation = useArchivePipeline();
  const restoreMutation = useRestorePipeline();
  const dismissMutation = useDismissJobNoGo();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>("pipeline");
  const [search, setSearch] = useState("");
  const [sortOption, setSortOption] = useState<EntitySortOption>(SORT_OPTIONS[2]);
  const [creatingJobId, setCreatingJobId] = useState<string | null>(null);
  const [archivingId, setArchivingId] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [dismissingJobId, setDismissingJobId] = useState<string | null>(null);
  const [confirmNoGoJobId, setConfirmNoGoJobId] = useState<string | null>(null);
  const [confirmArchivePipeline, setConfirmArchivePipeline] =
    useState<Pipeline | null>(null);
  const [actionError, setActionError] = useState<{
    message: string;
    pipelineId?: string;
  } | null>(null);

  const { viewMode, setViewMode, allowedModes } = useListViewMode({
    storageKey: "viewMode_pipeline",
    defaultMode: "table",
    allowedModes: [...ALLOWED_MODES],
  });

  const jobs = jobsData?.jobs ?? [];
  const pipelines = pipelinesData?.pipelines ?? [];

  const pipelineByJobId = useMemo(() => {
    const map: Record<string, Pipeline> = {};
    for (const p of pipelines) {
      if (p.jobId) map[p.jobId] = p;
    }
    return map;
  }, [pipelines]);

  const pipelineById = useMemo(() => {
    const map: Record<string, Pipeline> = {};
    for (const p of pipelines) {
      const id = pipelineRecordId(p);
      if (id) map[id] = p;
    }
    return map;
  }, [pipelines]);

  const pipedJobs = useMemo(() => jobs.filter((j) => j.pipelineId), [jobs]);
  /** Ready = same eligibility as Jobs Inbox (unpiped, not no-go/archived). */
  const readyJobs = useMemo(() => jobs.filter(isInboxJob), [jobs]);

  const activePipedJobs = useMemo(
    () =>
      pipedJobs.filter((job) => {
        const pipeline = getPipelineForJob(job, pipelines);
        return pipeline && !isPipelineArchived(pipeline);
      }),
    [pipedJobs, pipelines],
  );

  const archivedPipedJobs = useMemo(
    () =>
      pipedJobs.filter((job) => {
        const pipeline = getPipelineForJob(job, pipelines);
        return pipeline && isPipelineArchived(pipeline);
      }),
    [pipedJobs, pipelines],
  );

  const filteredJobs = useMemo(() => {
    let list =
      activeTab === "pipeline"
        ? activePipedJobs
        : activeTab === "archived"
          ? archivedPipedJobs
          : readyJobs;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (j) =>
          j.company?.toLowerCase().includes(q) ||
          j.role?.toLowerCase().includes(q),
      );
    }
    return applySort(list, sortOption);
  }, [
    activePipedJobs,
    archivedPipedJobs,
    readyJobs,
    activeTab,
    search,
    sortOption,
  ]);

  const {
    pageItems: pagedJobs,
    page,
    setPage,
    pageSize,
    setPageSize,
    totalPages,
    showingFrom,
    showingTo,
  } = useClientPagination(filteredJobs, [
    activeTab,
    search,
    sortOption.field,
    sortOption.order,
  ]);

  const getSortValue = useCallback((job: Job, key: string) => {
    if (key === "role") return displayRole(job);
    if (key === "score") return resolveJobScore(job) ?? -1;
    if (key === "updated") {
      return new Date(job.updatedAt ?? job.createdAt).getTime();
    }
    if (key === "stage") {
      const pipeline =
        pipelineByJobId[job.id] ??
        (job.pipelineId ? pipelineById[job.pipelineId] : undefined);
      return pipeline?.currentStage ?? job.stage ?? "";
    }
    return displayCompany(job);
  }, [pipelineByJobId, pipelineById]);

  const { sortedItems, sortKey, sortDirection, onSortChange } =
    useClientTableSort({
      items: pagedJobs,
      defaultKey: null,
      getValue: getSortValue,
    });

  const handleStartPipeline = (jobId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setCreatingJobId(jobId);
    createPipelineMutation.mutate(jobId, {
      onSuccess: (data) => {
        navigate(pipelineHref(data.pipeline.id, "Evaluated"));
        setCreatingJobId(null);
      },
      onError: () => setCreatingJobId(null),
    });
  };

  const runNoGo = (jobId: string) => {
    setDismissingJobId(jobId);
    setActionError(null);
    dismissMutation.mutate(
      { jobId },
      {
        onError: (err) => {
          const conflict = parseNoGoPipelineConflict(err);
          if (conflict) setActionError(conflict);
          else
            setActionError({
              message: err instanceof Error ? err.message : "No-go failed",
            });
        },
        onSettled: () => setDismissingJobId(null),
      },
    );
  };

  const handleArchive = (pipeline: Pipeline, e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmArchivePipeline(pipeline);
  };

  const runArchive = (pipeline: Pipeline) => {
    const id = pipelineRecordId(pipeline);
    setArchivingId(id);
    archiveMutation.mutate(id, {
      onSettled: () => setArchivingId(null),
    });
  };

  const handleRestore = (pipeline: Pipeline, e: React.MouseEvent) => {
    e.stopPropagation();
    const id = pipelineRecordId(pipeline);
    setRestoringId(id);
    restoreMutation.mutate(pipeline, {
      onSettled: () => setRestoringId(null),
    });
  };

  const isLoading = jobsLoading || pipelinesLoading;

  const resolvePipeline = (job: Job) =>
    pipelineByJobId[job.id] ??
    (job.pipelineId ? pipelineById[job.pipelineId] : undefined);

  const renderActions = (job: Job) => {
    const pipeline = resolvePipeline(job);
    const pipelineId =
      job.pipelineId ?? (pipeline ? pipelineRecordId(pipeline) : undefined);

    if (activeTab === "unpiped") {
      return (
        <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            data-testid="pipeline-ready-nogo"
            onClick={(e) => {
              e.stopPropagation();
              setConfirmNoGoJobId(job.id);
            }}
            disabled={dismissingJobId === job.id}
            className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted/20 disabled:opacity-50"
          >
            {dismissingJobId === job.id ? "…" : "No-go"}
          </button>
          <button
            type="button"
            data-testid="pipeline-ready-start"
            onClick={(e) => handleStartPipeline(job.id, e)}
            disabled={creatingJobId === job.id}
            className="rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-brand-foreground hover:opacity-90 disabled:opacity-50"
          >
            {creatingJobId === job.id ? "Starting…" : "Start pipeline"}
          </button>
        </div>
      );
    }
    if (activeTab === "pipeline" && pipeline && canArchivePipeline(pipeline)) {
      return (
        <button
          type="button"
          data-testid="pipeline-active-archive"
          onClick={(e) => handleArchive(pipeline, e)}
          disabled={archivingId === pipelineId}
          className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted/20 disabled:opacity-50"
        >
          {archivingId === pipelineId ? "Archiving…" : "Archive"}
        </button>
      );
    }
    if (activeTab === "archived" && pipeline) {
      return (
        <button
          type="button"
          onClick={(e) => handleRestore(pipeline, e)}
          disabled={restoringId === pipelineId}
          className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted/20 disabled:opacity-50"
        >
          {restoringId === pipelineId ? "Restoring…" : "Restore"}
        </button>
      );
    }
    return null;
  };

  const columns = useMemo((): Column<Job>[] => {
    const cols: Column<Job>[] = [
      {
        key: "company",
        header: "Company",
        sortable: true,
        render: (job) => {
          const pipeline = resolvePipeline(job);
          const pipelineId =
            job.pipelineId ?? (pipeline ? pipelineRecordId(pipeline) : undefined);
          const stage = pipeline?.currentStage ?? job.stage ?? "Evaluated";
          return pipelineId ? (
            <Link
              to={pipelineHref(pipelineId, stage)}
              onClick={(e) => e.stopPropagation()}
              className="font-medium text-foreground hover:text-brand hover:underline"
            >
              {displayCompany(job)}
            </Link>
          ) : (
            <span className="font-medium text-foreground">{displayCompany(job)}</span>
          );
        },
      },
      {
        key: "role",
        header: "Role",
        sortable: true,
        render: (job) => (
          <span className="text-muted-foreground">{displayRole(job)}</span>
        ),
      },
      {
        key: "stage",
        header: "Stage",
        sortable: true,
        render: (job) => {
          const pipeline = resolvePipeline(job);
          const stage = pipeline?.currentStage ?? job.stage ?? "Evaluated";
          return (
            <span className="text-foreground">{pipeline ? stage : "—"}</span>
          );
        },
      },
      {
        key: "score",
        header: "Score",
        sortable: true,
        render: (job) => {
          const displayScore = resolveJobScore(job);
          const tone =
            displayScore !== undefined
              ? displayScore >= 4
                ? "good"
                : displayScore >= 3
                  ? "warn"
                  : "bad"
              : "muted";
          return displayScore !== undefined ? (
            <span
              className={cn(
                "inline-flex rounded-full px-2 py-0.5 font-mono text-xs",
                TONE_BG[tone],
              )}
            >
              {displayScore}/5
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          );
        },
      },
      {
        key: "updated",
        header: "Updated",
        sortable: true,
        render: (job) => (
          <span className="text-muted-foreground">
            {new Date(job.updatedAt ?? job.createdAt).toLocaleDateString()}
          </span>
        ),
      },
      {
        key: "actions",
        header: "Action",
        className: "text-right",
        render: (job) =>
          renderActions(job) ?? (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
    ];
    return cols;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    creatingJobId,
    archivingId,
    restoringId,
    dismissingJobId,
    pipelineByJobId,
    pipelineById,
  ]);

  const tabButton = (
    id: Tab,
    label: string,
    count: number,
    glossaryKey: keyof typeof JOB_BUCKET_GLOSSARY,
  ) => (
    <button
      key={id}
      type="button"
      title={JOB_BUCKET_GLOSSARY[glossaryKey]}
      onClick={() => setActiveTab(id)}
      className={cn(
        "relative flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition",
        activeTab === id
          ? "text-foreground"
          : "text-muted-foreground hover:text-foreground",
      )}
    >
      {label}
      <span
        className={cn(
          "rounded-full px-1.5 py-0.5 text-xs",
          activeTab === id
            ? "bg-brand/20 text-brand"
            : "bg-muted/20 text-muted-foreground",
        )}
        title={JOB_BUCKET_GLOSSARY[glossaryKey]}
      >
        {count}
      </span>
      {activeTab === id && (
        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand" />
      )}
    </button>
  );

  return (
    <>
      <header className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Pipeline</h1>
            <p className="text-muted-foreground">
              Evaluate, apply, and track each opportunity through every stage.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/jobs"
              className="rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground transition hover:bg-muted/20"
            >
              Jobs
            </Link>
            <Link
              to="/jobs/add"
              className="rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground transition hover:bg-muted/20"
            >
              + Add Job
            </Link>
            <Link
              to="/explore"
              className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground transition hover:opacity-90"
            >
              Explore
            </Link>
          </div>
        </div>
      </header>

      <div className="mb-6 flex flex-wrap gap-1 border-b border-border">
        {tabButton("pipeline", "Active Pipelines", activePipedJobs.length, "active")}
        {tabButton("unpiped", "Ready to Start", readyJobs.length, "ready")}
        {tabButton("archived", "Archived", archivedPipedJobs.length, "archived")}
      </div>

      {actionError && (
        <div
          className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-foreground"
          role="alert"
          data-testid="pipeline-nogo-conflict"
        >
          <p>{actionError.message}</p>
          {actionError.pipelineId ? (
            <Link
              to={pipelineHref(actionError.pipelineId)}
              className="mt-2 inline-block text-sm font-medium text-brand hover:underline"
              data-testid="pipeline-discard-in-pipeline"
            >
              Discard in Pipeline
            </Link>
          ) : null}
          <button
            type="button"
            className="ml-3 text-xs text-muted-foreground hover:underline"
            onClick={() => setActionError(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="mb-4">
        <CollectionCommandBar
          searchQuery={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by company or role…"
          sortOption={sortOption}
          onSortChange={setSortOption}
          sortOptions={SORT_OPTIONS}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          allowedModes={allowedModes}
        />
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
        </div>
      )}

      {!isLoading && filteredJobs.length === 0 && (
        <div className="rounded-lg border border-border bg-card p-8 text-center">
          <p className="text-sm text-muted-foreground">
            {search
              ? "No jobs match your search."
              : activeTab === "pipeline"
                ? "No active pipelines. Start one from the Ready to Start tab."
                : activeTab === "archived"
                  ? "No archived pipelines yet. Archive finished pipelines to keep your active list focused."
                  : "Inbox / Ready is empty. Import jobs or explore new roles."}
          </p>
        </div>
      )}

      {!isLoading && filteredJobs.length > 0 && (
        <>
          <EntityListLayout
            viewMode={viewMode}
            table={
              <DataTable
                data={sortedItems}
                columns={columns}
                onRowClick={(job) => {
                  const pipeline = resolvePipeline(job);
                  const pipelineId =
                    job.pipelineId ??
                    (pipeline ? pipelineRecordId(pipeline) : undefined);
                  const stage =
                    pipeline?.currentStage ?? job.stage ?? "Evaluated";
                  if (pipelineId) navigate(pipelineHref(pipelineId, stage));
                }}
                sortKey={sortKey}
                sortDirection={sortDirection}
                onSortChange={onSortChange}
              />
            }
            list={
              <div className="space-y-2">
                {pagedJobs.map((job) => {
                  const pipeline = resolvePipeline(job);
                  const stage =
                    pipeline?.currentStage ?? job.stage ?? "Evaluated";
                  const displayScore = resolveJobScore(job);
                  return (
                    <EntityCompactItem
                      key={job.id}
                      id={job.id}
                      title={displayCompany(job)}
                      description={displayRole(job)}
                      badges={[
                        ...(pipeline
                          ? [
                              {
                                label: stage,
                                colorClass: "bg-muted/30 text-muted-foreground",
                              },
                            ]
                          : []),
                        ...(displayScore !== undefined
                          ? [
                              {
                                label: `${displayScore}/5`,
                                colorClass: TONE_BG[
                                  displayScore >= 4
                                    ? "good"
                                    : displayScore >= 3
                                      ? "warn"
                                      : "bad"
                                ],
                              },
                            ]
                          : []),
                      ]}
                      actions={renderActions(job)}
                      onClick={() => {
                        const pipelineId =
                          job.pipelineId ??
                          (pipeline ? pipelineRecordId(pipeline) : undefined);
                        if (pipelineId) navigate(pipelineHref(pipelineId, stage));
                      }}
                    />
                  );
                })}
              </div>
            }
            cards={
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {pagedJobs.map((job) => {
                  const pipeline = resolvePipeline(job);
                  const stage =
                    pipeline?.currentStage ?? job.stage ?? "Evaluated";
                  const displayScore = resolveJobScore(job);
                  return (
                    <EntityCard
                      key={job.id}
                      id={job.id}
                      title={displayCompany(job)}
                      description={displayRole(job)}
                      primaryBadge={
                        pipeline
                          ? {
                              label: stage,
                              colorClass: "bg-muted/30 text-muted-foreground",
                            }
                          : undefined
                      }
                      secondaryBadge={
                        displayScore !== undefined
                          ? {
                              label: `${displayScore}/5`,
                              colorClass:
                                TONE_BG[
                                  displayScore >= 4
                                    ? "good"
                                    : displayScore >= 3
                                      ? "warn"
                                      : "bad"
                                ],
                            }
                          : undefined
                      }
                      metadata={[
                        {
                          value: new Date(
                            job.updatedAt ?? job.createdAt,
                          ).toLocaleDateString(),
                        },
                      ]}
                      actions={renderActions(job)}
                      onClick={() => {
                        const pipelineId =
                          job.pipelineId ??
                          (pipeline ? pipelineRecordId(pipeline) : undefined);
                        if (pipelineId) navigate(pipelineHref(pipelineId, stage));
                      }}
                    />
                  );
                })}
              </div>
            }
          />
          <PaginationBar
            page={page}
            pageSize={pageSize}
            totalItems={filteredJobs.length}
            totalPages={totalPages}
            showingFrom={showingFrom}
            showingTo={showingTo}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </>
      )}

      <ConfirmDialog
        open={confirmNoGoJobId !== null}
        title="Mark as no-go?"
        description="This job will leave Ready and Jobs Inbox."
        confirmLabel="No-go"
        confirmTone="danger"
        busy={dismissingJobId !== null}
        testId="pipeline-nogo-confirm"
        onCancel={() => setConfirmNoGoJobId(null)}
        onConfirm={() => {
          if (!confirmNoGoJobId) return;
          const id = confirmNoGoJobId;
          setConfirmNoGoJobId(null);
          runNoGo(id);
        }}
      />

      <ConfirmDialog
        open={confirmArchivePipeline !== null}
        title="Archive this pipeline?"
        description="It will move to the Archived tab."
        confirmLabel="Archive"
        confirmTone="danger"
        busy={archivingId !== null}
        testId="pipeline-archive-confirm"
        onCancel={() => setConfirmArchivePipeline(null)}
        onConfirm={() => {
          if (!confirmArchivePipeline) return;
          const p = confirmArchivePipeline;
          setConfirmArchivePipeline(null);
          runArchive(p);
        }}
      />
    </>
  );
}
